import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { FileUpload, Script, ScriptBreakdown, ExecutionResult, scriptApi, workflowApi, workflowContextApi } from '../services/api';
import FileManager from './FileManager';
import ScriptEditor from './ScriptEditor';
import StreamingScriptEditor from './StreamingScriptEditor';
import { ScriptBreakdown as ScriptBreakdownView } from './ScriptBreakdown';
import { BreakdownStatus } from './BreakdownStatus';
import ResultsView from './ResultsView';
import ExecutionHistory from './ExecutionHistory';
import ErrorBoundary from './ErrorBoundary';
import { SaveWorkflowModal } from './SaveWorkflowModal';
import { WorkflowUATModal } from './WorkflowUATModal';
import { CodeDiffViewer } from './CodeDiffViewer';
import { MetadataEditor } from './MetadataEditor';
import { MetadataReviewData } from '../types/columnMapping';
import { ColumnSelectionInfo } from '../types/columnSelection';
import { ChevronDown, Save, GitCompare, RefreshCw, Info } from 'lucide-react';
import { MetadataModal } from './MetadataModal';
import { ScriptVersionSelector } from './ScriptVersionSelector';
import { ScriptVersionHistory } from './ScriptVersionHistory';
import WorkflowMetadataTab from './WorkflowMetadataTab';
import { useWorkflowMetadata } from '../contexts/WorkflowMetadataContext';
import { useWorkflowMetadataFetch } from '../hooks/useWorkflowMetadataFetch';
import './WorkflowSidebar.css';

interface WorkflowSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  activeTab: string;
  onTabChange: (tab: string) => void;
  
  // Data props
  sessionId: string;
  uploadedFiles: FileUpload[];
  currentScript: Script | null;
  scriptBreakdown: ScriptBreakdown | null;
  isAdaptedBreakdown?: boolean;
  executionResult: ExecutionResult | null;
  workflowContextId?: string | null;
  currentPhase?: number;
  
  // Event handlers
  onFilesChanged: (files: FileUpload[]) => void;
  onScriptExecuted: (result: ExecutionResult) => void;
  onBreakdownUpdated?: (breakdown: ScriptBreakdown) => void;
  onWorkflowRestart?: () => void;
  onFixError?: (errorMessage: string) => void;
  onScriptChange?: (script: Script) => void;
}

export interface WorkflowSidebarHandle {
  startScriptStreaming: () => void;
  appendScriptChunk: (chunk: string) => void;
  endScriptStreaming: (script: Script | null) => void;
  showDiff: (diffData: any) => void;
  setBreakdownTimeout: () => void;
  setPhaseExecuting: (executing: boolean) => void;
}

const SIDEBAR_MIN_WIDTH = 400;
const SIDEBAR_DEFAULT_WIDTH = 800;
const SIDEBAR_MAX_WIDTH = 1200;

const WorkflowSidebar = forwardRef<WorkflowSidebarHandle, WorkflowSidebarProps>(({
  isOpen,
  onClose,
  activeTab,
  onTabChange,
  sessionId,
  uploadedFiles,
  currentScript,
  scriptBreakdown,
  isAdaptedBreakdown = false,
  executionResult,
  workflowContextId,
  currentPhase = 1,
  onFilesChanged,
  onScriptExecuted,
  onBreakdownUpdated,
  onWorkflowRestart,
  onFixError,
  onScriptChange,
}, ref) => {
  const [executing, setExecuting] = useState(false);
  const [isRegeneratingBreakdown, setIsRegeneratingBreakdown] = useState(false);
  const [breakdownIsStale, setBreakdownIsStale] = useState(false);
  const [breakdownLoadingState, setBreakdownLoadingState] = useState<'idle' | 'generating' | 'timeout'>('idle');
  const [breakdownGenerationStartTime, setBreakdownGenerationStartTime] = useState<Date | null>(null);
  const [breakdownStatus, setBreakdownStatus] = useState<string>('pending');
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('workflowSidebarWidth');
    return saved ? parseInt(saved, 10) : SIDEBAR_DEFAULT_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);
  
  // Save workflow state
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveType, setSaveType] = useState<'draft' | 'final'>('draft');
  const [showUATModal, setShowUATModal] = useState(false);
  const [pendingWorkflowId, setPendingWorkflowId] = useState<string | null>(null);
  const [showSaveDropdown, setShowSaveDropdown] = useState(false);
  
  // Workflow context data for saving
  const [workflowContextData, setWorkflowContextData] = useState<{
    requirements_analysis?: string;
    column_mappings?: Record<string, string>;
    qa_analysis?: string;
  }>({});
  
  // Streaming state
  const [isStreamingScript, setIsStreamingScript] = useState(false);
  const [streamingCode, setStreamingCode] = useState('');
  const streamingEditorRef = useRef<{ appendStreamChunk: (chunk: string) => void }>(null);
  const [isMetadataCollapsed, setIsMetadataCollapsed] = useState(true); // Start collapsed
  
  // Diff view state
  const [diffData, setDiffData] = useState<any>(null);
  const [showDiff, setShowDiff] = useState(false);
  
  // Metadata Editor state
  const [showMetadataEditor, setShowMetadataEditor] = useState(false);
  const [metadataReviewData, setMetadataReviewData] = useState<MetadataReviewData | null>(null);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
  const [columnSelectionInfo, setColumnSelectionInfo] = useState<ColumnSelectionInfo | null>(null);
  const [isExecutingPhase, setIsExecutingPhase] = useState(false);
  const editColumnsInProgressRef = useRef(false);
  
  // Metadata modal state
  const [showMetadataModal, setShowMetadataModal] = useState(false);
  
  // Version history modal state
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showExecutionHistory, setShowExecutionHistory] = useState(false);
  
  // Fetch metadata when metadata tab is active
  // Only enable polling when metadata tab is visible to save resources
  const isMetadataTabActive = activeTab === 'metadata';
  useWorkflowMetadataFetch(workflowContextId || undefined, {
    enabled: isMetadataTabActive && !!workflowContextId,
    pollingInterval: 3000,
    enablePolling: true
  });

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement;
        // Don't close if clicking on chat area
        if (!target.closest('.chat-container') && !target.closest('.centered-chat-input')) {
          onClose();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Keyboard shortcut to toggle sidebar
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '.') {
        e.preventDefault();
        if (isOpen) {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, onClose]);

  // Handle resize
  const handleResizeStart = (e: React.MouseEvent) => {
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = sidebarWidth;
    e.preventDefault();
  };

  useEffect(() => {
    if (!isResizing) return;

    // Add global cursor style
    document.body.style.cursor = 'col-resize';

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = startXRef.current - e.clientX; // Inverted because sidebar is on the right
      const newWidth = Math.min(
        SIDEBAR_MAX_WIDTH,
        Math.max(SIDEBAR_MIN_WIDTH, startWidthRef.current + deltaX)
      );
      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      localStorage.setItem('workflowSidebarWidth', sidebarWidth.toString());
      document.body.style.cursor = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
    };
  }, [isResizing, sidebarWidth]);

  const tabs = [
    { id: 'files', label: 'Files', icon: '📁', show: true },
    { id: 'script', label: 'Script', icon: '📝', show: !!currentScript || isStreamingScript || !!streamingCode },
    { id: 'process', label: 'Process Flow', icon: '🔄', show: !!currentScript }, // Show when script exists
    { id: 'results', label: 'Results', icon: '📊', show: !!executionResult },
    { id: 'metadata', label: 'Metadata', icon: '🔍', show: true }, // Always visible as requested
  ];

  const visibleTabs = tabs.filter(tab => tab.show);

  // Handle save workflow completion
  const handleSaveComplete = (workflowId: string, requiresUAT?: boolean) => {
    if (requiresUAT) {
      setPendingWorkflowId(workflowId);
      setShowUATModal(true);
    } else {
      // Draft saved successfully
      alert('Workflow saved as draft successfully!');
    }
  };
  
  // Fetch workflow context data when save modal opens
  const handleOpenSaveModal = async (type: 'draft' | 'final') => {
    setSaveType(type);
    
    // If we have a workflow context, fetch its data
    if (workflowContextId) {
      try {
        const response = await workflowContextApi.get(workflowContextId);
        const contextData = response.data.data;
        
        setWorkflowContextData({
          requirements_analysis: contextData.requirements_analysis,
          column_mappings: contextData.column_mappings,
          qa_analysis: contextData.qa_analysis,
        });
      } catch (error) {
        console.error('Failed to fetch workflow context:', error);
        // Continue with save even if context fetch fails
        setWorkflowContextData({});
      }
    }
    
    setShowSaveModal(true);
  };

  // Handle UAT completion
  const handleUATComplete = () => {
    alert('Workflow published successfully!');
    setShowUATModal(false);
    setPendingWorkflowId(null);
    // Clear workflow name from session storage
    sessionStorage.removeItem('pending_workflow_name');
    sessionStorage.removeItem('pending_workflow_id');
  };

  // Log when scriptBreakdown prop changes
  useEffect(() => {
    console.log('WorkflowSidebar scriptBreakdown changed:', scriptBreakdown ? 'Has breakdown' : 'No breakdown');
  }, [scriptBreakdown]);

  // Smart polling for breakdown status
  useEffect(() => {
    if (!currentScript?.script_id) return;
    
    let pollCount = 0;
    let pollInterval: NodeJS.Timeout | null = null;
    const maxPolls = 30; // Max 30 polls (about 2 minutes with exponential backoff)
    
    const checkBreakdownStatus = async () => {
      try {
        const response = await scriptApi.getBreakdownStatus(currentScript.script_id);
        const status = response.data;
        
        setBreakdownIsStale(status.is_stale);
        setBreakdownStatus(status.status);
        
        // Update loading state based on status
        if (status.status === 'generating') {
          setBreakdownLoadingState('generating');
          // Set start time if not already set
          if (!breakdownGenerationStartTime) {
            setBreakdownGenerationStartTime(new Date());
          }
          
          // Continue polling with exponential backoff
          pollCount++;
          if (pollCount < maxPolls) {
            const nextDelay = Math.min(1000 * Math.pow(1.5, Math.min(pollCount - 1, 10)), 10000); // Cap at 10 seconds
            pollInterval = setTimeout(checkBreakdownStatus, nextDelay);
          } else {
            // Max polls reached, show timeout state
            setBreakdownLoadingState('timeout');
          }
        } else if (status.status === 'completed' && status.has_breakdown) {
          // Breakdown is ready, fetch it
          setBreakdownLoadingState('idle');
          setBreakdownGenerationStartTime(null); // Reset timer
          
          // If we don't have the breakdown yet, refresh it
          if (!scriptBreakdown) {
            console.log('Fetching breakdown for script:', currentScript.script_id);
            // Fetch the script with breakdown
            scriptApi.get(currentScript.script_id)
              .then(response => {
                console.log('Fetched script with breakdown:', response.data.breakdown ? 'Yes' : 'No');
                if (response.data.breakdown && onBreakdownUpdated) {
                  console.log('Calling onBreakdownUpdated with breakdown');
                  onBreakdownUpdated(response.data.breakdown);
                }
              })
              .catch(error => {
                console.error('Failed to fetch breakdown:', error);
              });
          }
        } else if (status.status === 'failed') {
          // Generation failed
          setBreakdownLoadingState('timeout');
          console.error('Breakdown generation failed:', status.last_error);
        } else if (status.status === 'pending') {
          // Not started yet, begin polling
          setBreakdownLoadingState('generating');
          // Set start time for pending state too
          if (!breakdownGenerationStartTime) {
            setBreakdownGenerationStartTime(new Date());
          }
          pollCount++;
          if (pollCount < maxPolls) {
            pollInterval = setTimeout(checkBreakdownStatus, 2000); // Start with 2 second delay
          }
        }
      } catch (error) {
        console.error('Failed to check breakdown status:', error);
        // On error, try a few more times before giving up
        if (pollCount < 3) {
          pollCount++;
          pollInterval = setTimeout(checkBreakdownStatus, 5000);
        }
      }
    };
    
    // Initial check
    checkBreakdownStatus();
    
    // Cleanup
    return () => {
      if (pollInterval) {
        clearTimeout(pollInterval);
      }
    };
  }, [currentScript?.script_id, currentScript?.code, scriptBreakdown, onBreakdownUpdated, breakdownGenerationStartTime]);
  
  // Timer effect to update elapsed time
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    
    if (breakdownGenerationStartTime && breakdownLoadingState === 'generating') {
      const updateElapsedTime = () => {
        const now = new Date();
        const elapsed = Math.floor((now.getTime() - breakdownGenerationStartTime.getTime()) / 1000);
        setElapsedTime(elapsed);
      };
      
      // Update immediately and then every second
      updateElapsedTime();
      timer = setInterval(updateElapsedTime, 1000);
    } else {
      setElapsedTime(0);
    }
    
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [breakdownGenerationStartTime, breakdownLoadingState]);
  
  // Check if current script is a revision and load diff data
  useEffect(() => {
    const loadDiffData = async () => {
      if (currentScript?.parent_script_id && !diffData) {
        try {
          // Fetch parent script to create diff
          const parentResponse = await scriptApi.get(currentScript.parent_script_id);
          if (parentResponse.data) {
            const parentScript = parentResponse.data;
            // Create diff data
            const newDiffData = {
              originalCode: parentScript.code,
              adaptedCode: currentScript.code,
              changes: [], // Would need actual diff calculation
              preservationRatio: 0.85, // Placeholder
              visualDiff: null
            };
            setDiffData(newDiffData);
            console.log('Loaded diff data from parent script:', currentScript.parent_script_id);
          }
        } catch (error) {
          console.error('Failed to load parent script for diff:', error);
        }
      }
    };
    
    loadDiffData();
  }, [currentScript?.script_id, currentScript?.parent_script_id]);
  
  // Load column info when workflow context changes
  useEffect(() => {
    if (workflowContextId && workflowContextId !== '' && uploadedFiles.length > 0) {
      let cancelled = false;
      
      // Fetch column info without opening the editor
      const loadColumnInfo = async () => {
        try {
          const response = await fetch(`/api/workflow/context/${workflowContextId}/metadata-review`);
          
          // Check if request was cancelled
          if (cancelled) return;
          
          if (!response.ok) {
            throw new Error(`Failed to fetch column info: ${response.status}`);
          }
          
          const data = await response.json();
          
          // Check again if cancelled before setting state
          if (cancelled) return;
          
          if (data.reviewData) {
            setColumnSelectionInfo({
              totalColumns: data.reviewData.total_columns || 0,
              selectedColumns: data.reviewData.selected_columns || 0,
              hasBeenModified: data.hasBeenModified || false
            });
          }
        } catch (error) {
          if (!cancelled) {
            console.error('Failed to load column info:', error);
            // Don't set column info on error - leave it as null
          }
        }
      };
      
      loadColumnInfo();
      
      // Cleanup function to cancel if component unmounts or deps change
      return () => {
        cancelled = true;
      };
    } else {
      // Clear column info if no context or files
      setColumnSelectionInfo(null);
    }
  }, [workflowContextId, uploadedFiles.length]);
  
  // Update loading state based on script and breakdown
  useEffect(() => {
    if (currentScript && !scriptBreakdown) {
      setBreakdownLoadingState('generating');
    } else if (scriptBreakdown) {
      setBreakdownLoadingState('idle');
    }
  }, [currentScript, scriptBreakdown]);

  const handleRegenerateBreakdown = async () => {
    if (!currentScript?.script_id) return;
    
    setIsRegeneratingBreakdown(true);
    try {
      const response = await scriptApi.regenerateBreakdown(currentScript.script_id, true);
      // Update the breakdown in parent component
      if (response.data.breakdown && onBreakdownUpdated) {
        onBreakdownUpdated(response.data.breakdown);
        setBreakdownIsStale(false);
      }
    } catch (error) {
      console.error('Failed to regenerate breakdown:', error);
      alert('Failed to regenerate breakdown. Please try again.');
    } finally {
      setIsRegeneratingBreakdown(false);
    }
  };

  const handleRefreshBreakdown = async () => {
    if (!currentScript?.script_id) return;
    
    setIsRegeneratingBreakdown(true);
    try {
      const response = await scriptApi.get(currentScript.script_id);
      // Update the breakdown in parent component
      if (response.data.breakdown && onBreakdownUpdated) {
        onBreakdownUpdated(response.data.breakdown);
        setBreakdownLoadingState('idle');
        setBreakdownIsStale(false);
      } else {
        console.log('No breakdown found in script data');
      }
    } catch (error) {
      console.error('Failed to refresh breakdown:', error);
      alert('Failed to refresh breakdown. Please try again.');
    } finally {
      setIsRegeneratingBreakdown(false);
    }
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    startScriptStreaming: () => {
      setIsStreamingScript(true);
      setStreamingCode('');
      // Automatically switch to script tab
      onTabChange('script');
    },
    
    appendScriptChunk: (chunk: string) => {
      setStreamingCode(prev => prev + chunk);
      // Also append to the streaming editor if it's mounted
      streamingEditorRef.current?.appendStreamChunk(chunk);
    },
    
    endScriptStreaming: (script: Script | null) => {
      setIsStreamingScript(false);
      // Keep the streamed code visible until a proper script is set
      // The script will be set by the parent component through props
      
      // If no script was provided but we have streamed content, keep it visible
      if (!script && streamingCode) {
        console.warn('Phase 2 ended without a valid script, preserving streamed content');
      }
    },
    
    showDiff: (data: any) => {
      setDiffData(data);
      setShowDiff(true);
      onTabChange('script'); // Switch to script tab to show diff
    },
    
    setBreakdownTimeout: () => {
      setBreakdownLoadingState('timeout');
    },
    
    setPhaseExecuting: (executing: boolean) => {
      setIsExecutingPhase(executing);
    }
  }));
  
  // Check if workflow can be saved
  const canSaveWorkflow = currentScript || uploadedFiles.length > 0 || streamingCode.length > 0;
  
  // Function to manually continue workflow from Phase 2
  const handleContinueWorkflow = async () => {
    if (!workflowContextId) {
      alert('No workflow context found. Please start a new workflow.');
      return;
    }
    
    try {
      // Note: currentPhase is a prop, not state - we can't set it directly
      // The parent component should handle phase changes
      setIsExecutingPhase(true);
      
      // Directly call Phase 2
      const response = await fetch(`/api/workflow/phase2/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          context_id: workflowContextId
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to continue workflow: ${response.statusText}`);
      }
      
      // Handle streaming response
      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');
      
      const decoder = new TextDecoder();
      setStreamingCode('');
      setIsStreamingScript(true);
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (line.trim()) {
            try {
              const data = JSON.parse(line);
              if (data.type === 'script_chunk') {
                setStreamingCode(prev => prev + data.content);
              } else if (data.type === 'script_complete') {
                setIsStreamingScript(false);
                // Script ID is saved, parent component will handle loading if needed
                console.log('Script generation complete:', data.script_id);
              }
            } catch (e) {
              console.error('Error parsing phase 2 response:', e);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error continuing workflow:', error);
      alert(`Failed to continue workflow: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsExecutingPhase(false);
    }
  };
  
  // Handle Edit Columns button click
  const handleEditColumns = async () => {
    if (!workflowContextId || workflowContextId === '') {
      console.error('No valid workflow context ID available');
      alert('Please start a workflow before editing columns');
      return;
    }
    
    if (isExecutingPhase) {
      console.warn('Cannot edit columns while a phase is executing');
      return;
    }
    
    // Prevent concurrent operations
    if (editColumnsInProgressRef.current) {
      console.warn('Edit columns operation already in progress');
      return;
    }
    
    editColumnsInProgressRef.current = true;
    setIsLoadingMetadata(true);
    try {
      // Fetch current metadata review state
      const response = await fetch(`/api/workflow/context/${workflowContextId}/metadata-review`);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch metadata review state: ${response.status} ${errorText}`);
      }
      
      const data = await response.json();
      
      // Update column selection info
      if (data.reviewData) {
        setColumnSelectionInfo({
          totalColumns: data.reviewData.total_columns || 0,
          selectedColumns: data.reviewData.selected_columns || 0,
          hasBeenModified: data.hasBeenModified || false
        });
      }
      
      setMetadataReviewData(data.reviewData);
      setShowMetadataEditor(true);
    } catch (error) {
      console.error('Error fetching metadata review state:', error);
      
      // More specific error messages
      if (error instanceof TypeError && error.message.includes('fetch')) {
        alert('Network error: Unable to connect to server. Please check your connection.');
      } else if (error instanceof Error && error.message.includes('404')) {
        alert('Workflow context not found. It may have expired. Please restart your workflow.');
      } else {
        alert(`Failed to load column selection: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    } finally {
      setIsLoadingMetadata(false);
      editColumnsInProgressRef.current = false;
    }
  };
  
  // Handle metadata editor confirm
  const handleMetadataConfirm = async () => {
    setShowMetadataEditor(false);
    
    // Reload column info to get updated counts
    if (workflowContextId && uploadedFiles.length > 0) {
      try {
        const response = await fetch(`/api/workflow/context/${workflowContextId}/metadata-review`);
        if (response.ok) {
          const data = await response.json();
          if (data.reviewData) {
            setColumnSelectionInfo({
              totalColumns: data.reviewData.total_columns || 0,
              selectedColumns: data.reviewData.selected_columns || 0,
              hasBeenModified: data.hasBeenModified || true
            });
          }
        }
      } catch (error) {
        console.error('Failed to reload column info after update:', error);
      }
    }
    
    // If we're past phase 2, we need to restart the workflow
    if (currentPhase >= 2) {
      if (onWorkflowRestart) {
        onWorkflowRestart();
      }
    }
  };
  
  // Check if columns have been selected
  const hasColumnSelection = workflowContextId !== null && columnSelectionInfo !== null && columnSelectionInfo.selectedColumns > 0;

  return (
    <>
      {/* Peek strip */}
      <div 
        className={`workflow-peek ${isOpen ? 'hidden' : ''} ${(uploadedFiles.length > 0 || currentScript || executionResult) ? 'has-content' : ''}`}
        onClick={() => onTabChange('files')}
        title="View workflow (Cmd/Ctrl + .)"
      >
        <div className="peek-icon">
          ←
        </div>
      </div>

      {/* Backdrop */}
      {isOpen && <div className="workflow-backdrop" onClick={onClose} />}

      {/* Sidebar */}
      <div 
        ref={sidebarRef} 
        className={`workflow-sidebar ${isOpen ? 'open' : ''} ${isResizing ? 'resizing' : ''}`}
        style={{ width: `${sidebarWidth}px` }}
      >
        {/* Resize handle */}
        <div 
          className="workflow-resize-handle"
          onMouseDown={handleResizeStart}
        />
        <div className="sidebar-header">
          <h2 className="workflow-title">Workflow</h2>
          <div className="header-actions">
            {currentScript && (
              <button 
                className="btn btn-primary btn-sm execute-button"
                onClick={async () => {
                  setExecuting(true);
                  try {
                    // Check if this is a temporary or local script ID
                    if (currentScript.script_id.startsWith('temp-') || currentScript.script_id.startsWith('local-') || currentScript.script_id === 'streaming-script') {
                      // For local scripts, try to save them to the backend first
                      console.log('Local script detected, attempting to save to backend...');
                      try {
                        const savedScript = await scriptApi.save(sessionId, currentScript.code);
                        console.log('Script saved successfully:', savedScript.data);
                        // Execute the saved script
                        const response = await scriptApi.execute(savedScript.data.script_id, sessionId);
                        onScriptExecuted(response.data);
                      } catch (saveError) {
                        console.error('Failed to save script to backend:', saveError);
                        alert('Unable to save script to server. Please check your connection and try again.');
                      }
                      return;
                    }
                    
                    const response = await scriptApi.execute(currentScript.script_id, sessionId);
                    onScriptExecuted(response.data);
                  } catch (error: any) {
                    console.error('Failed to execute script:', error);
                    if (error.response?.status === 404) {
                      alert('Script not found. The server may have restarted. Please regenerate the script from the chat.');
                    } else {
                      alert('Failed to execute script. Please check the console for details.');
                    }
                  } finally {
                    setExecuting(false);
                  }
                }}
                disabled={executing}
              >
                {executing ? (
                  <>
                    <span className="spinner-small"></span>
                    <span>Executing...</span>
                  </>
                ) : executionResult ? 'Re-execute' : 'Execute'}
              </button>
            )}
            {canSaveWorkflow && (
              <div className="save-buttons-group">
                <button 
                  className="btn btn-outline btn-sm save-draft-button"
                  onClick={() => handleOpenSaveModal('draft')}
                  title="Save as draft to continue working later"
                >
                  <Save size={14} />
                  <span>Draft</span>
                </button>
                <button 
                  className="btn btn-secondary btn-sm save-final-button"
                  onClick={() => handleOpenSaveModal('final')}
                  title="Save as final and publish for team use"
                >
                  <Save size={14} />
                  <span>Final</span>
                </button>
              </div>
            )}
            <button className="btn btn-ghost btn-icon btn-sm close-button" onClick={onClose}>
              ×
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="workflow-tabs">
          {visibleTabs.map(tab => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => onTabChange(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
              {tab.id === 'process' && currentScript && !scriptBreakdown && (
                <span className="loading-indicator">•</span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'files' && (
            <div className="files-tab">
              {uploadedFiles.length === 0 ? (
                <div className="empty-state" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '400px',
                  padding: '40px',
                  textAlign: 'center',
                  color: '#6c757d'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }}>📁</div>
                  <h3 style={{ color: '#495057', marginBottom: '8px', fontSize: '18px', fontWeight: '500' }}>No Files Uploaded</h3>
                  <p style={{ fontSize: '14px', maxWidth: '300px', lineHeight: '1.5' }}>
                    Upload Excel, CSV files or PDF documents from the chat area to begin your workflow.
                  </p>
                </div>
              ) : (
                <ErrorBoundary 
                  componentName="File Manager"
                  fallback={
                    <div className="error-fallback">
                      <p>File manager encountered an error.</p>
                      <button onClick={() => window.location.reload()}>Reload</button>
                    </div>
                  }
                >
                  <FileManager
                    sessionId={sessionId}
                    onFilesChanged={onFilesChanged}
                    externalFiles={uploadedFiles}
                    onEditColumns={(!workflowContextId || workflowContextId === '' || isLoadingMetadata || isExecutingPhase || editColumnsInProgressRef.current) ? undefined : handleEditColumns}
                    hasColumnSelection={hasColumnSelection}
                    columnSelectionInfo={columnSelectionInfo}
                  />
                </ErrorBoundary>
              )}
            </div>
          )}

          {activeTab === 'script' && (currentScript || isStreamingScript || streamingCode) && (
            <div className="script-tab">
              {/* Script Metadata for Audit - Collapsible */}
              {currentScript && (
                <div style={{
                  background: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  marginBottom: '16px',
                  fontSize: '13px',
                  fontFamily: 'Monaco, Menlo, monospace',
                  color: '#495057',
                  overflow: 'hidden'
                }}>
                  {/* Collapsible Header */}
                  <div 
                    onClick={() => setIsMetadataCollapsed(!isMetadataCollapsed)}
                    style={{
                      padding: '10px 16px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderBottom: isMetadataCollapsed ? 'none' : '1px solid #dee2e6',
                      userSelect: 'none'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ 
                        fontSize: '14px',
                        transform: isMetadataCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                        display: 'inline-block'
                      }}>
                        ▼
                      </span>
                      <strong style={{ color: '#495057', fontSize: '13px' }}>Script Metadata</strong>
                      {isMetadataCollapsed && (
                        <span style={{ 
                          color: '#6c757d', 
                          fontSize: '12px',
                          marginLeft: '8px'
                        }}>
                          v{currentScript.version || 1} • {currentScript.script_id.slice(0, 8)}...
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Collapsible Content */}
                  {!isMetadataCollapsed && (
                    <div style={{ padding: '12px 16px' }}>
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: '10px'
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <strong style={{ color: '#6c757d', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Script ID</strong>
                          <span style={{ color: '#212529', fontSize: '13px', fontFamily: 'Monaco, Menlo, monospace', wordBreak: 'break-all' }}>
                            {currentScript.script_id}
                          </span>
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div>
                            <strong style={{ color: '#6c757d' }}>Version:</strong>
                            <span style={{ 
                              marginLeft: '8px', 
                              color: '#212529',
                              background: currentScript.parent_script_id ? '#fff3cd' : '#d1ecf1',
                              padding: '2px 6px',
                              borderRadius: '3px',
                              fontWeight: '600'
                            }}>
                              v{currentScript.version || 1}
                            </span>
                          </div>
                          
                          {/* Version control buttons */}
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => setShowVersionHistory(true)}
                              style={{
                                padding: '4px 8px',
                                fontSize: '11px',
                                background: 'transparent',
                                border: '1px solid #dee2e6',
                                borderRadius: '3px',
                                cursor: 'pointer',
                                color: '#495057',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                              title="View version history"
                            >
                              <GitCompare size={12} />
                              History
                            </button>
                          </div>
                        </div>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <strong style={{ color: '#6c757d', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Session ID</strong>
                          <span style={{ color: '#212529', fontSize: '13px', fontFamily: 'Monaco, Menlo, monospace', wordBreak: 'break-all' }}>
                            {currentScript.session_id}
                          </span>
                        </div>
                        
                        {currentScript.workflow_context_id && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <strong style={{ color: '#6c757d', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Context ID</strong>
                            <span style={{ color: '#212529', fontSize: '13px', fontFamily: 'Monaco, Menlo, monospace', wordBreak: 'break-all' }}>
                              {currentScript.workflow_context_id}
                            </span>
                          </div>
                        )}
                        
                        {currentScript.parent_script_id && (
                          <div 
                            title={`Full ID: ${currentScript.parent_script_id}`} 
                            style={{ 
                              cursor: 'help',
                              gridColumn: 'span 2',
                              padding: '8px',
                              background: '#fff3cd',
                              borderRadius: '4px',
                              border: '1px solid #ffeaa7'
                            }}
                          >
                            <strong style={{ color: '#856404' }}>📝 Revised from:</strong>
                            <span style={{ marginLeft: '8px', color: '#856404', fontWeight: '500' }}>
                              {currentScript.parent_script_id.slice(0, 12)}...
                            </span>
                          </div>
                        )}
                        
                        {currentScript.created_at && (
                          <div style={{ 
                            marginTop: '8px', 
                            paddingTop: '8px', 
                            borderTop: '1px solid #dee2e6',
                            fontSize: '11px',
                            color: '#6c757d'
                          }}>
                            <strong>Created:</strong> {new Date(currentScript.created_at).toLocaleString()}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Diff toggle button */}
              {diffData && currentScript && (
                <div className="script-tab-header">
                  <button
                    className={`diff-toggle-btn ${showDiff ? 'active' : ''}`}
                    onClick={() => setShowDiff(!showDiff)}
                    title="Toggle diff view"
                  >
                    <GitCompare size={16} />
                    {showDiff ? 'Hide Diff' : 'Show Diff'}
                  </button>
                </div>
              )}
              
              {/* Content based on mode */}
              {showDiff && diffData ? (
                <CodeDiffViewer
                  originalCode={diffData.originalCode}
                  adaptedCode={diffData.adaptedCode}
                  changes={diffData.changes}
                  preservationRatio={diffData.preservationRatio}
                  visualDiff={diffData.visualDiff}
                />
              ) : isStreamingScript || (!currentScript && streamingCode) ? (
                <StreamingScriptEditor
                  ref={streamingEditorRef}
                  initialCode={streamingCode}
                  language="python"
                  isStreaming={isStreamingScript}
                  onCodeChange={(code) => setStreamingCode(code)}
                />
              ) : (
                currentScript && (
                  <ScriptEditor
                    script={currentScript}
                    sessionId={sessionId}
                  />
                )
              )}
            </div>
          )}

          {activeTab === 'process' && (
            <div className="process-tab">
              {!currentScript ? (
                <div className="empty-state" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '400px',
                  padding: '40px',
                  textAlign: 'center',
                  color: '#6c757d'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }}>🔄</div>
                  <h3 style={{ color: '#495057', marginBottom: '8px', fontSize: '18px', fontWeight: '500' }}>No Process Flow Yet</h3>
                  <p style={{ fontSize: '14px', maxWidth: '300px', lineHeight: '1.5' }}>
                    Generate a script first to see the visual process flow of your data transformation.
                  </p>
                </div>
              ) : (
                <>
                  {/* Show breakdown if available */}
                  {scriptBreakdown ? (
                    <ScriptBreakdownView 
                      breakdown={scriptBreakdown} 
                      isAdapted={isAdaptedBreakdown}
                      isStale={breakdownIsStale}
                      onRegenerate={handleRegenerateBreakdown}
                      scriptMetadata={currentScript ? {
                        scriptId: currentScript.script_id,
                        version: currentScript.version || 1,
                        createdAt: currentScript.created_at,
                        parentScriptId: currentScript.parent_script_id || undefined
                      } : undefined}
                    />
                  ) : (
                    <div className="breakdown-loading">
                      <div className="loading-spinner"></div>
                      <p>Loading process overview...</p>
                      <p className="loading-subtext">
                        The breakdown will appear here once it's ready
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === 'results' && (
            <div className="results-tab">
              {!executionResult ? (
                <div className="empty-state" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '400px',
                  padding: '40px',
                  textAlign: 'center',
                  color: '#6c757d'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }}>📊</div>
                  <h3 style={{ color: '#495057', marginBottom: '8px', fontSize: '18px', fontWeight: '500' }}>No Results Yet</h3>
                  <p style={{ fontSize: '14px', maxWidth: '300px', lineHeight: '1.5' }}>
                    Execute a script using the "Execute" button above to see the output here.
                  </p>
                </div>
              ) : (
                <ResultsView 
                  result={executionResult} 
                  onFixWithAssertion={onFixError}
                  onShowHistory={() => setShowExecutionHistory(true)}
                />
              )}
            </div>
          )}
          
          {activeTab === 'metadata' && (
            <div className="metadata-tab">
              <WorkflowMetadataTab 
                sessionId={sessionId}
                contextId={workflowContextId}
                scriptId={currentScript?.script_id}
                currentPhase={currentPhase}
              />
            </div>
          )}
        </div>
      </div>

      {/* Save Workflow Modal */}
      {showSaveModal && (
        <SaveWorkflowModal
          isOpen={showSaveModal}
          onClose={() => setShowSaveModal(false)}
          sessionId={sessionId}
          saveType={saveType}
          onSaveComplete={handleSaveComplete}
          currentScript={currentScript?.code || streamingCode}
          currentBreakdown={scriptBreakdown}
          // Pass complete state for self-contained drafts
          workflowContextId={workflowContextId || undefined}
          currentPhase={currentPhase}
          fileMetadata={uploadedFiles.map(f => ({
            file_id: f.file_id,
            file_name: f.filename,
            file_type: f.file_type,
            size: f.size_bytes
          }))}
          columnMappings={workflowContextData.column_mappings || 
            (metadataReviewData?.mappings ? 
              metadataReviewData.mappings.reduce((acc, mapping) => {
                acc[mapping.original] = mapping.metadata;
                return acc;
              }, {} as Record<string, string>) : undefined)}
          requirementsAnalysis={workflowContextData.requirements_analysis}
          qaResults={workflowContextData.qa_analysis ? 
            { analysis: workflowContextData.qa_analysis } : undefined}
        />
      )}

      {/* UAT Modal */}
      {showUATModal && pendingWorkflowId && (
        <WorkflowUATModal
          isOpen={showUATModal}
          onClose={() => {
            setShowUATModal(false);
            setPendingWorkflowId(null);
          }}
          workflowId={pendingWorkflowId}
          workflowName={sessionStorage.getItem('pending_workflow_name') || 'Workflow'}
          onComplete={handleUATComplete}
        />
      )}
      
      {/* Metadata Editor */}
      {showMetadataEditor && metadataReviewData && workflowContextId && (
        <MetadataEditor
          reviewData={metadataReviewData}
          contextId={workflowContextId}
          sessionId={sessionId}
          onConfirm={handleMetadataConfirm}
          onCancel={() => setShowMetadataEditor(false)}
          currentPhase={currentPhase}
          hasGeneratedScript={currentScript !== null}
          hasExecuted={executionResult !== null}
        />
      )}
      
      {/* Metadata Modal */}
      {showMetadataModal && currentScript && (
        <MetadataModal
          isOpen={showMetadataModal}
          onClose={() => setShowMetadataModal(false)}
          sessionId={sessionId}
          scriptId={currentScript.script_id}
          contextId={workflowContextId || ''}
        />
      )}
      
      {/* Version History Modal */}
      {showVersionHistory && currentScript && (
        <ScriptVersionHistory
          isOpen={showVersionHistory}
          onClose={() => setShowVersionHistory(false)}
          currentScript={currentScript}
          onVersionLoad={(version: Script) => {
            // Handle loading a different version
            console.log('Loading version:', version);
            if (onScriptChange) {
              onScriptChange(version);
            }
            setShowVersionHistory(false);
          }}
        />
      )}
      
      {showExecutionHistory && currentScript && (
        <ExecutionHistory
          scriptId={currentScript.script_id}
          onClose={() => setShowExecutionHistory(false)}
          onSelectExecution={async (executionId) => {
            // Fetch and display the selected execution result
            try {
              const response = await fetch(`/api/scripts/execution/${executionId}/result`);
              if (response.ok) {
                const result = await response.json();
                onScriptExecuted(result);
                setShowExecutionHistory(false);
                onTabChange('results');
              }
            } catch (error) {
              console.error('Failed to load execution result:', error);
            }
          }}
        />
      )}
    </>
  );
});

WorkflowSidebar.displayName = 'WorkflowSidebar';

export default WorkflowSidebar;